/* platform/Organizations — tenant list with create modal + status toggle.
   Row click → /platform/organizations/:orgId. Clean ESM. */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { StatCard, PageHead, Th, TenancyModal } from './_ui';
import { statusTone, PLAN_TONES } from './platformStore';
import * as orgApi from '../api/organization.api.js';
import { exportCSV, exportExcel, COLS } from './utils/exportUtils.js';

const STATUS_LABEL = { ACTIVE: 'Active', DRAFT: 'Draft', SUSPENDED: 'Suspended', ORG_ADMIN_PENDING: 'Pending Setup', ARCHIVED: 'Archived' };

const COUNTRY_OPTIONS = [
  { label: 'India',     value: 'IN' },
  { label: 'UAE',       value: 'AE' },
  { label: 'Singapore', value: 'SG' },
  { label: 'USA',       value: 'US' },
];

const TIMEZONE_OPTIONS = [
  { label: 'IST — Asia/Kolkata',         value: 'Asia/Kolkata' },
  { label: 'GST — Asia/Dubai',           value: 'Asia/Dubai' },
  { label: 'SGT — Asia/Singapore',       value: 'Asia/Singapore' },
  { label: 'EST — America/New_York',     value: 'America/New_York' },
  { label: 'PST — America/Los_Angeles',  value: 'America/Los_Angeles' },
];

const BILLING_CYCLE_OPTIONS = [
  { label: 'Monthly',  value: 'MONTHLY' },
  { label: 'Annual',   value: 'ANNUAL'  },
  { label: 'Custom',   value: 'CUSTOM'  },
];

const STEP_LABELS = ['Info', 'Plan & Billing', 'Licenses', 'Review'];

const STANDARD_LICENSE_TYPES = ['USER', 'WORKSPACE', 'AGENT', 'BOT', 'STORAGE', 'API', 'MESSAGE'];

const FALLBACK_ALLOTMENTS = {
  USER: 5, WORKSPACE: 3, AGENT: 2, BOT: 5, STORAGE: 5, API: 1000, MESSAGE: 10000,
};

const LIC_META = {
  USER:      { label: 'User Seats',       unit: 'seats',     planField: null                  },
  WORKSPACE: { label: 'Workspaces',       unit: 'workspaces', planField: 'maxWorkspaces'       },
  AGENT:     { label: 'AI Agents',        unit: 'agents',    planField: 'maxAgents'            },
  BOT:       { label: 'Bots',             unit: 'bots',      planField: 'maxBots'              },
  STORAGE:   { label: 'Storage (GB)',     unit: 'GB',        planField: 'maxStorageGb'         },
  API:       { label: 'API Calls / Day',  unit: 'calls',     planField: 'maxApiCallsPerDay'    },
  MESSAGE:   { label: 'Messages / Month', unit: 'messages',  planField: 'maxMessagesPerMonth'  },
};

/* Exact value the backend will write to license.allotted — mirrors OrgProvisioningService resolvedLicenses. */
const getPlanAllocation = (type, plan) => {
  if (!plan) return FALLBACK_ALLOTMENTS[type];
  const { planField } = LIC_META[type];
  if (!planField) return FALLBACK_ALLOTMENTS[type]; // USER — plan has no maxUsers field
  const v = plan[planField];
  return v ?? FALLBACK_ALLOTMENTS[type];
};

/* What the plan allows for this type: null = unlimited, number = capped, undefined = no plan field. */
const getPlanCeiling = (type, plan) => {
  if (!plan) return undefined;
  const { planField } = LIC_META[type];
  if (!planField) return undefined;
  return plan[planField];
};

const EMPTY_WIZARD = {
  step: 1,
  /* Step 1 — Org info */
  name: '', region: '', industry: '', country: 'IN', timezone: 'Asia/Kolkata',
  /* Step 2 — Plan + billing (currency is derived from the plan, not configurable here) */
  planId: null, planName: '',
  billingCycle: 'MONTHLY', contractRef: '', startDate: '', renewsAt: '',
  /* Step 3 — Per-type license overrides; empty map = use plan defaults for all types */
  licOverrides: {},
};

export default function Organizations({ data }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = window.useAuth();
  const isSAOrPA = user?.platformRole === 'SUPER_ADMIN' || user?.platformRole === 'PLATFORM_ADMIN';

  const [q, setQ]                       = useState('');
  const [editing, setEditing]           = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter]     = useState('');
  const [sortBy, setSortBy]             = useState('created');
  const [sortDir, setSortDir]           = useState('desc');
  const { organizations } = data.state;

  const planOptions = [...new Set(organizations.map(o => o.plan).filter(Boolean))].sort();

  useEffect(() => {
    if (location.state?.openCreate) {
      setEditing({ ...EMPTY_WIZARD });
    }
  }, [location.state?.openCreate]);

  let rows = organizations
    .filter(o => !q || o.name.toLowerCase().includes(q.toLowerCase()) || (o.region || '').toLowerCase().includes(q.toLowerCase()))
    .filter(o => !statusFilter || o.status === statusFilter)
    .filter(o => !planFilter   || o.plan   === planFilter);

  const dir = sortDir === 'asc' ? 1 : -1;
  rows = [...rows].sort((a, b) => {
    if (sortBy === 'name')    return dir * a.name.localeCompare(b.name);
    if (sortBy === 'status')  return dir * (a.status  || '').localeCompare(b.status  || '');
    if (sortBy === 'plan')    return dir * (a.plan    || '').localeCompare(b.plan    || '');
    if (sortBy === 'created') return dir * (a.created || '').localeCompare(b.created || '');
    return 0;
  });

  const toggleStatus = async (org) => {
    const next = org.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    try {
      if (next === 'ACTIVE') await orgApi.activate(org.id);
      else await orgApi.suspend(org.id);
      data.update(s => ({ ...s, organizations: s.organizations.map(o => o.id === org.id ? { ...o, status: next } : o) }));
      data.addAudit({ action: next === 'ACTIVE' ? 'activated org' : 'suspended org', target: org.name, cat: 'Org', sev: next === 'ACTIVE' ? 'info' : 'warning' });
      toast({ tone: next === 'ACTIVE' ? 'success' : 'warning', icon: 'AlertCircle', title: 'Organization ' + (next === 'ACTIVE' ? 'Activated' : 'Suspended') });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err.message });
    }
  };

  const licFor = (orgId) => {
    const userLic = data.state.licenses.find(x => x.org === orgId && x.type === 'USER');
    return { used: userLic?.used ?? 0, allotted: userLic?.purchased ?? 0 };
  };

  const handleCreate = async () => {
    const selectedPlan = data.state.plans.find(p => p.id === editing.planId);
    try {
      await orgApi.provisionOrg({
        name:     editing.name,
        industry: editing.industry,
        region:   editing.region   || undefined,
        country:  editing.country,
        timezone: editing.timezone,
        planId:   editing.planId,
        billing: {
          cycle:       editing.billingCycle || 'MONTHLY',
          contractRef: editing.contractRef  || undefined,
          startsAt:    editing.startDate    || undefined,
          renewalAt:   editing.renewsAt     || undefined,
        },
        // All 7 types sent as explicit overrides so backend uses exactly what the grid shows.
        licenseOverrides: STANDARD_LICENSE_TYPES.map(type => {
          const val = editing.licOverrides[type];
          const allotted = (val !== undefined && val !== '' && parseInt(val, 10) > 0)
            ? parseInt(val, 10)
            : getPlanAllocation(type, selectedPlan);
          return { type, allotted };
        }),
      });

      await data.reload();
      data.addAudit({ action: 'provisioned org', target: editing.name, cat: 'Org', sev: 'info' });
      toast({ tone: 'success', icon: 'Check', title: 'Organization Created', desc: `${editing.name} provisioned with ${editing.planName || 'selected'} plan.` });
      setEditing(null);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Provisioning failed', desc: err.message });
    }
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Organizations"
        sub={organizations.length + ' tenants · ' + organizations.filter(o => o.status === 'ACTIVE').length + ' active'}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="Download" onClick={() => exportCSV('organizations', rows, COLS.organizations)}>CSV</Btn>
            <Btn variant="ghost" icon="FileSpreadsheet" onClick={() => exportExcel('organizations', [{ name: 'Organizations', rows, columns: COLS.organizations }])}>Excel</Btn>
            <Btn variant="primary" icon="Plus" onClick={() => setEditing({ ...EMPTY_WIZARD })}>Create Organization</Btn>
          </div>
        }
      />

      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <StatCard label="Total"       value={organizations.length}                                       icon="Building2"   color="#125AB0" />
        <StatCard label="Active"      value={organizations.filter(o => o.status === 'ACTIVE').length}     sub={organizations.filter(o => o.status === 'DRAFT').length + ' draft'} icon="Check" color="#0E8C5A" />
        <StatCard label="Suspended"   value={organizations.filter(o => o.status === 'SUSPENDED').length} icon="AlertCircle" color="#E08A00" />
        <StatCard label="Total Users" value={organizations.reduce((s, o) => s + o.users, 0).toLocaleString()} icon="Users" color="#6E3FBA" />
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="nx-search-inline" style={{ width: 260 }}>
          <NexusIcon name="Search" size={13} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search organizations…" />
        </div>
        <select className="nx-input" style={{ width: 160, fontSize: 13 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="DRAFT">Draft</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="ORG_ADMIN_PENDING">Pending Setup</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        <select className="nx-input" style={{ width: 150, fontSize: 13 }} value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
          <option value="">All Plans</option>
          {planOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="nx-input" style={{ width: 160, fontSize: 13 }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="created">Sort: Created</option>
          <option value="name">Sort: Name</option>
          <option value="status">Sort: Status</option>
          <option value="plan">Sort: Plan</option>
        </select>
        <button
          className="nx-btn nx-btn-ghost nx-btn-sm"
          title={sortDir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
          onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          style={{ padding: '5px 8px' }}>
          <NexusIcon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
        </button>
        {(q || statusFilter || planFilter) && (
          <button className="nx-btn nx-btn-ghost nx-btn-sm" onClick={() => { setQ(''); setStatusFilter(''); setPlanFilter(''); }}>
            Clear filters
          </button>
        )}
      </div>

      <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
        <thead><tr>
          <Th icon="Building2">Organization</Th>
          <Th icon="Tag">Plan</Th>
          <Th icon="Package">Subscription</Th>
          <Th icon="Key">Seats</Th>
          <Th icon="Calendar">Renewal</Th>
          <Th icon="Activity">Status</Th>
          <Th end />
        </tr></thead>
        <tbody>
          {rows.map(o => {
            const lic = licFor(o.id);
            const renewalDate = o.renewalAt ? String(o.renewalAt).split('T')[0] : null;
            const daysToRenewal = renewalDate ? Math.ceil((new Date(renewalDate) - Date.now()) / 86400000) : null;
            let renewalTone = 'gray';
            let renewalLabel = renewalDate;
            if (daysToRenewal !== null) {
              if (daysToRenewal < 0)    { renewalTone = 'red';   renewalLabel = 'Expired'; }
              else if (daysToRenewal <= 30) { renewalTone = 'amber'; renewalLabel = daysToRenewal + 'd'; }
              else                          { renewalTone = 'green'; }
            }
            return (
              <tr key={o.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => navigate('/platform/organizations/' + o.id)}>
                <td>
                  <span className="nx-row-name">
                    <span className="nx-domain-mark" style={{ width: 24, height: 24, fontSize: 11 }}>{o.name[0]}</span>
                    <div><span className="nx-row-name-text">{o.name}</span><div className="nx-muted nx-mini">{o.region} · {o.industry}</div></div>
                  </span>
                </td>
                <td><Pill tone={PLAN_TONES[o.plan] || 'gray'}>{o.plan}</Pill></td>
                <td>
                  {o.subscriptionStatus
                    ? <Pill tone={statusTone(o.subscriptionStatus)}>{o.subscriptionStatus}</Pill>
                    : <span className="nx-muted nx-mini">—</span>}
                </td>
                <td><span className="nx-cell is-mono">{lic.used}/{lic.allotted || '—'}</span></td>
                <td>
                  {renewalLabel
                    ? <Pill tone={renewalTone}>{renewalLabel}</Pill>
                    : <span className="nx-muted nx-mini">—</span>}
                </td>
                <td><Pill tone={statusTone(o.status)}>{STATUS_LABEL[o.status] || o.status.replaceAll('_', ' ')}</Pill></td>
                <td className="nx-td-end" style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                  <Btn variant="ghost" size="sm" icon="ArrowRight" onClick={() => navigate('/platform/organizations/' + o.id)}>View</Btn>
                  {isSAOrPA && (
                    <Btn variant="ghost" size="sm" icon="CreditCard" onClick={() => navigate('/platform/organizations/' + o.id + '?tab=commercial')}>Commercial</Btn>
                  )}
                  {(o.status === 'ACTIVE' || o.status === 'SUSPENDED') && (
                    <Btn variant="ghost" size="sm" icon={o.status === 'SUSPENDED' ? 'Check' : 'AlertCircle'} onClick={() => toggleStatus(o)}>{o.status === 'SUSPENDED' ? 'Activate' : 'Suspend'}</Btn>
                  )}
                  {isSAOrPA && o.status === 'ARCHIVED' && (
                    <Btn variant="ghost" size="sm" icon="RotateCcw" onClick={async () => {
                      try {
                        await orgApi.restore(o.id);
                        data.update(s => ({ ...s, organizations: s.organizations.map(x => x.id === o.id ? { ...x, status: 'SUSPENDED' } : x) }));
                        data.addAudit({ action: 'restored org', target: o.name, cat: 'Org', sev: 'info' });
                        toast({ tone: 'success', icon: 'RotateCcw', title: 'Organization Restored', desc: o.name + ' is now suspended.' });
                      } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Restore failed', desc: err.message }); }
                    }}>Restore</Btn>
                  )}
                  {isSAOrPA && o.status !== 'ARCHIVED' && (
                    <Btn variant="ghost" size="sm" icon="Archive" onClick={async () => {
                      try {
                        await orgApi.archive(o.id);
                        data.update(s => ({ ...s, organizations: s.organizations.map(x => x.id === o.id ? { ...x, status: 'ARCHIVED' } : x) }));
                        data.addAudit({ action: 'archived org', target: o.name, cat: 'Org', sev: 'warning' });
                        toast({ tone: 'warning', icon: 'Archive', title: 'Organization Archived', desc: o.name });
                      } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: err.message }); }
                    }}>Archive</Btn>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--nx-text-muted)', fontSize: 13 }}>
              No organizations match the current filters.
            </td></tr>
          )}
        </tbody>
      </table></div></div>

      {editing && (
        <TenancyModal
          title="Create Organization"
          icon="Building2"
          dialog
          width={560}
          subtitle={`Step ${editing.step} of ${STEP_LABELS.length} — ${STEP_LABELS[editing.step - 1]}`}
          onClose={() => setEditing(null)}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              {editing.step === 1
                ? <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
                : <Btn variant="ghost" onClick={() => setEditing({ ...editing, step: editing.step - 1 })}>Back</Btn>
              }
              {editing.step < 4 && (
                <Btn variant="primary" icon="ChevronRight"
                  disabled={
                    (editing.step === 1 && (!editing.name?.trim() || !editing.industry?.trim())) ||
                    (editing.step === 2 && !editing.planId)
                  }
                  onClick={() => setEditing({ ...editing, step: editing.step + 1 })}>
                  Next
                </Btn>
              )}
              {editing.step === 4 && (
                <Btn variant="primary" icon="Check" onClick={handleCreate}>
                  Create Organization
                </Btn>
              )}
            </div>
          }
        >
          {/* Step progress indicator */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
            {STEP_LABELS.map((label, i) => (
              <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                  background: editing.step > i + 1 ? 'var(--nx-accent)' : editing.step === i + 1 ? 'var(--nx-accent)' : 'var(--nx-border)',
                  color: editing.step >= i + 1 ? '#fff' : 'var(--nx-text-muted)',
                }}>
                  {editing.step > i + 1 ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: 10, color: editing.step === i + 1 ? 'var(--nx-accent)' : 'var(--nx-text-muted)' }}>{label}</span>
              </div>
            ))}
          </div>

          {/* ── Step 1: Org Info ── */}
          {editing.step === 1 && (
            <>
              <div className="nx-form-row"><label>Name *</label><input className="nx-input" autoFocus value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Apex Finserv" /></div>
              <div className="nx-form-row"><label>Industry *</label><input className="nx-input" value={editing.industry} onChange={e => setEditing({ ...editing, industry: e.target.value })} placeholder="e.g. FinTech" /></div>
              <div className="nx-form-row"><label>Region</label><input className="nx-input" value={editing.region} onChange={e => setEditing({ ...editing, region: e.target.value })} placeholder="Optional" /></div>
              <div className="nx-form-row"><label>Country</label>
                <select className="nx-input" value={editing.country} onChange={e => setEditing({ ...editing, country: e.target.value })}>
                  {COUNTRY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="nx-form-row"><label>Timezone</label>
                <select className="nx-input" value={editing.timezone} onChange={e => setEditing({ ...editing, timezone: e.target.value })}>
                  {TIMEZONE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </>
          )}

          {/* ── Step 2: Plan + Billing ── */}
          {editing.step === 2 && (
            <>
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 12 }}>Select a plan and configure billing details for this organization.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 14 }}>
                {data.state.plans.filter(p => p.status === 'ACTIVE').length === 0 && (
                  <p style={{ gridColumn: '1/-1', fontSize: 12, color: 'var(--nx-text-muted)', margin: 0 }}>Loading available plans…</p>
                )}
                {data.state.plans.filter(p => p.status === 'ACTIVE').map(plan => (
                  <div key={plan.id}
                    onClick={() => setEditing({ ...editing, planId: plan.id, planName: plan.name, licOverrides: {} })}
                    style={{
                      border: `2px solid ${editing.planId === plan.id ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
                      borderRadius: 8, padding: 12, cursor: 'pointer',
                      background: editing.planId === plan.id ? 'var(--nx-accent-tint,rgba(18,90,176,0.06))' : 'var(--nx-surface)',
                    }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{plan.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                      {plan.priceMonthly != null ? `${plan.currency || 'INR'} ${plan.priceMonthly.toLocaleString()}/mo` : 'Custom pricing'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>
                      {plan.maxWorkspaces ? `Up to ${plan.maxWorkspaces} workspaces` : 'Unlimited workspaces'}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Billing Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
                  <div className="nx-form-row" style={{ gridColumn: '1/-1' }}>
                    <label>Billing Cycle</label>
                    <select className="nx-input" value={editing.billingCycle} onChange={e => setEditing({ ...editing, billingCycle: e.target.value })}>
                      {BILLING_CYCLE_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                  </div>
                  <div className="nx-form-row">
                    <label>Start Date</label>
                    <input className="nx-input" type="date" value={editing.startDate} onChange={e => setEditing({ ...editing, startDate: e.target.value })} />
                  </div>
                  <div className="nx-form-row">
                    <label>Renewal Date</label>
                    <input className="nx-input" type="date" value={editing.renewsAt} onChange={e => setEditing({ ...editing, renewsAt: e.target.value })} />
                  </div>
                  <div className="nx-form-row" style={{ gridColumn: '1/-1' }}>
                    <label>Contract Reference</label>
                    <input className="nx-input" value={editing.contractRef} onChange={e => setEditing({ ...editing, contractRef: e.target.value })} placeholder="e.g. CONTRACT-2026-001" />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Step 3: License Allocation Grid ── */}
          {editing.step === 3 && (() => {
            const selectedPlan = data.state.plans.find(p => p.id === editing.planId);
            return (
              <>
                <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 12 }}>
                  Pre-filled from plan defaults. Modify allotments that need to differ for this organization.
                  All values are written to the database exactly as shown.
                </p>
                <div style={{ border: '1px solid var(--nx-border)', borderRadius: 8, overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', background: 'var(--nx-surface-alt)', padding: '7px 12px', borderBottom: '1px solid var(--nx-border)' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>License Type</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center' }}>Plan Limit</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'right' }}>Allotment</span>
                  </div>
                  {STANDARD_LICENSE_TYPES.map((type, idx) => {
                    const meta        = LIC_META[type];
                    const ceiling     = getPlanCeiling(type, selectedPlan);
                    const defaultVal  = getPlanAllocation(type, selectedPlan);
                    const rawOverride = editing.licOverrides[type];
                    const inputVal    = rawOverride !== undefined ? rawOverride : String(defaultVal);
                    const parsed      = parseInt(inputVal, 10);
                    const isModified  = rawOverride !== undefined && rawOverride !== '' && !isNaN(parsed) && parsed !== defaultVal;
                    return (
                      <div key={type} style={{
                        display: 'grid', gridTemplateColumns: '1fr 110px 110px', alignItems: 'center',
                        padding: '7px 12px',
                        borderBottom: idx < STANDARD_LICENSE_TYPES.length - 1 ? '1px solid var(--nx-border)' : 'none',
                        background: idx % 2 === 0 ? 'var(--nx-bg)' : 'var(--nx-surface)',
                      }}>
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{meta.label}</span>
                          {type === 'USER' && (
                            <span style={{ fontSize: 10, color: 'var(--nx-text-faint)', display: 'block', marginTop: 1 }}>No plan limit · SA-configured</span>
                          )}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <span style={{ fontSize: 12, color: ceiling === null ? 'var(--nx-accent)' : ceiling === undefined ? 'var(--nx-text-faint)' : 'var(--nx-text-muted)' }}>
                            {ceiling === undefined ? '—' : ceiling === null ? 'Unlimited' : ceiling.toLocaleString()}
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <input
                            type="number"
                            min={1}
                            className="nx-input"
                            style={{
                              width: 90, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13,
                              borderColor: isModified ? 'var(--nx-accent)' : undefined,
                              fontWeight: isModified ? 700 : undefined,
                            }}
                            value={inputVal}
                            onChange={e => setEditing(prev => ({
                              ...prev,
                              licOverrides: { ...prev.licOverrides, [type]: e.target.value },
                            }))}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p style={{ fontSize: 11, color: 'var(--nx-text-faint)', marginTop: 8 }}>
                  Highlighted values override plan defaults. For unlimited plan fields, a conservative trial default is used until you increase it.
                </p>
              </>
            );
          })()}

          {/* ── Step 4: Review ── */}
          {editing.step === 4 && (() => {
            const selectedPlan = data.state.plans.find(p => p.id === editing.planId);

            // Exact license allotments — identical formula to handleCreate and the backend transaction.
            const reviewLicenses = STANDARD_LICENSE_TYPES.map(type => {
              const val     = editing.licOverrides[type];
              const parsed  = val !== undefined && val !== '' ? parseInt(val, 10) : NaN;
              const allotted = (!isNaN(parsed) && parsed > 0) ? parsed : getPlanAllocation(type, selectedPlan);
              return { type, allotted };
            });

            const fmtQuota = v => v != null ? v.toLocaleString() : 'Unlimited';

            const SectionHead = ({ children }) => (
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 14, marginBottom: 5 }}>
                {children}
              </div>
            );
            const KV = ({ label, value, mono }) => (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, padding: '3px 0', borderBottom: '1px solid var(--nx-border-faint,rgba(0,0,0,0.05))' }}>
                <span style={{ color: 'var(--nx-text-muted)', flexShrink: 0, marginRight: 12 }}>{label}</span>
                <span style={{ color: 'var(--nx-text)', fontWeight: 500, fontFamily: mono ? 'monospace' : undefined, textAlign: 'right' }}>{value}</span>
              </div>
            );

            return (
              <div style={{ fontSize: 13 }}>
                <SectionHead>Organization</SectionHead>
                <KV label="Name"     value={editing.name} />
                <KV label="Industry" value={editing.industry} />
                <KV label="Country"  value={COUNTRY_OPTIONS.find(c => c.value === editing.country)?.label || editing.country} />
                <KV label="Timezone" value={editing.timezone} />
                {editing.region && <KV label="Region" value={editing.region} />}

                <SectionHead>Plan &amp; Subscription</SectionHead>
                <KV label="Plan"           value={selectedPlan?.name || editing.planName || '—'} />
                <KV label="Tier"           value={selectedPlan?.tier || '—'} />
                <KV label="Currency"       value={selectedPlan?.currency || '—'} />
                <KV label="Initial Status" value="TRIAL" />
                <KV label="Billing Cycle"  value={editing.billingCycle || 'MONTHLY'} />
                {editing.startDate   && <KV label="Start Date"    value={editing.startDate} />}
                {editing.renewsAt    && <KV label="Renewal Date"  value={editing.renewsAt} />}
                {editing.contractRef && <KV label="Contract Ref"  value={editing.contractRef} mono />}

                <SectionHead>License Allocation</SectionHead>
                <div style={{ border: '1px solid var(--nx-border)', borderRadius: 6, overflow: 'hidden' }}>
                  {reviewLicenses.map(({ type, allotted }, idx) => (
                    <div key={type} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '5px 10px', fontSize: 12,
                      background: idx % 2 === 0 ? 'var(--nx-bg)' : 'var(--nx-surface)',
                      borderBottom: idx < reviewLicenses.length - 1 ? '1px solid var(--nx-border)' : 'none',
                    }}>
                      <span style={{ color: 'var(--nx-text-muted)' }}>{LIC_META[type].label}</span>
                      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--nx-text)' }}>
                        {allotted.toLocaleString()} {LIC_META[type].unit}
                      </span>
                    </div>
                  ))}
                </div>

                <SectionHead>Org Resource Quotas</SectionHead>
                <KV label="Max Workspaces" value={fmtQuota(selectedPlan?.maxWorkspaces)} />
                <KV label="Max AI Agents"  value={fmtQuota(selectedPlan?.maxAgents)} />
                <KV label="Max Bots"       value={fmtQuota(selectedPlan?.maxBots)} />
                <KV label="Org Status"     value="DRAFT (until org admin is provisioned)" />
              </div>
            );
          })()}
        </TenancyModal>
      )}
    </div>
  );
}
